import { Role, ROLES } from './roles';

/**
 * Define modular capabilities that components/actions can check.
 */
export const PERMISSIONS = {
  // CRM
  VIEW_LEADS: 'VIEW_LEADS',
  EDIT_LEADS: 'EDIT_LEADS',
  DELETE_LEADS: 'DELETE_LEADS',
  VIEW_DEALS: 'VIEW_DEALS',
  EDIT_DEALS: 'EDIT_DEALS',
  
  // Scraper
  RUN_SCRAPER: 'RUN_SCRAPER',
  
  // Dashboard & Analytics
  VIEW_COMMAND_CENTER: 'VIEW_COMMAND_CENTER',
  VIEW_OWNER_STATS: 'VIEW_OWNER_STATS',
  
  // HR / Workforce
  VIEW_TEAM_ATTENDANCE: 'VIEW_TEAM_ATTENDANCE',
  MANAGE_USERS: 'MANAGE_USERS',
  
  // General
  VIEW_NOTEPAD: 'VIEW_NOTEPAD',
  VIEW_ACTIVITY_FEED: 'VIEW_ACTIVITY_FEED',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/**
 * Role to Permission mapping
 * This defines the baseline access for each role.
 */
export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  [ROLES.SUPERADMIN]: Object.values(PERMISSIONS),
  [ROLES.ADMIN]: [
    PERMISSIONS.VIEW_LEADS,
    PERMISSIONS.EDIT_LEADS,
    PERMISSIONS.VIEW_DEALS,
    PERMISSIONS.EDIT_DEALS,
    PERMISSIONS.VIEW_TEAM_ATTENDANCE,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.VIEW_NOTEPAD,
    PERMISSIONS.VIEW_ACTIVITY_FEED,
  ],
  [ROLES.DEPARTMENT_ADMIN]: [
    PERMISSIONS.VIEW_LEADS,
    PERMISSIONS.EDIT_LEADS,
    PERMISSIONS.VIEW_DEALS,
    PERMISSIONS.EDIT_DEALS,
    PERMISSIONS.VIEW_TEAM_ATTENDANCE,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.VIEW_NOTEPAD,
    PERMISSIONS.VIEW_ACTIVITY_FEED,
  ],
  [ROLES.MANAGER]: [
    PERMISSIONS.VIEW_LEADS,
    PERMISSIONS.EDIT_LEADS,
    PERMISSIONS.VIEW_DEALS,
    PERMISSIONS.EDIT_DEALS,
    PERMISSIONS.VIEW_TEAM_ATTENDANCE,
    PERMISSIONS.VIEW_ACTIVITY_FEED,
  ],
  [ROLES.TEAM_LEAD]: [
    PERMISSIONS.VIEW_LEADS,
    PERMISSIONS.VIEW_DEALS,
    PERMISSIONS.VIEW_TEAM_ATTENDANCE,
    PERMISSIONS.VIEW_ACTIVITY_FEED,
  ],
  [ROLES.BDE]: [
    PERMISSIONS.VIEW_LEADS,
    PERMISSIONS.VIEW_DEALS,
    PERMISSIONS.VIEW_ACTIVITY_FEED,
  ],
  /**
   * This map is typed `Record<Role, Permission[]>`, so omitting
   * `sales_coordinator` did not fail the build — `RoleContext` falls back to an
   * empty array, which meant every `can(...)` check in the app answered false
   * for Rukmini's account and 3 other screens hid their actions from her. Her
   * real, wider invoice/document authority comes from the server catalogue
   * (`src/authority/permission-catalog.js`); this entry only restores the
   * legacy UI affordances to the same level a BDE gets.
   */
  [ROLES.SALES_COORDINATOR]: [
    PERMISSIONS.VIEW_LEADS,
    PERMISSIONS.VIEW_DEALS,
    PERMISSIONS.VIEW_ACTIVITY_FEED,
  ],
  [ROLES.DEVELOPER]: [
    PERMISSIONS.VIEW_LEADS,
    PERMISSIONS.VIEW_DEALS,
  ],
  [ROLES.EMPLOYEE]: [
    PERMISSIONS.VIEW_LEADS,
    PERMISSIONS.VIEW_DEALS,
    PERMISSIONS.VIEW_NOTEPAD,
    PERMISSIONS.VIEW_ACTIVITY_FEED,
    PERMISSIONS.RUN_SCRAPER, // Per backend rules, employees can run scraper
  ],
  // None of these — a driver's only screen is the Driver Portal, which
  // reads its own dedicated /api/fleet endpoints, not this CRM permission set.
  [ROLES.DRIVER]: [],
  // Field Executive: works their own leads / deals (the action-level rules are
  // in the server catalogue — authority/permission-catalog.js).
  [ROLES.FIELD_EXECUTIVE]: [
    PERMISSIONS.VIEW_LEADS,
    PERMISSIONS.EDIT_LEADS,
    PERMISSIONS.VIEW_DEALS,
    PERMISSIONS.VIEW_NOTEPAD,
    PERMISSIONS.VIEW_ACTIVITY_FEED,
  ],
};

/**
 * Checks if a role has a specific permission.
 */
export function hasPermission(role: Role | undefined, permission: Permission): boolean {
  if (!role) return false;
  if (role === ROLES.SUPERADMIN) return true;
  return ROLE_PERMISSIONS[role]?.includes(permission) ?? false;
}

/**
 * Checks if a role is equal to or greater than the required role.
 * SUPERADMIN > ADMIN > EMPLOYEE
 */
export function hasMinimumRole(userRole: Role | undefined, requiredRole: Role): boolean {
  if (!userRole) return false;
  if (userRole === ROLES.SUPERADMIN) return true;
  if (requiredRole === ROLES.EMPLOYEE) return true; // All valid roles have at least EMPLOYEE
  if (requiredRole === ROLES.ADMIN && (userRole === ROLES.ADMIN || userRole === ROLES.DEPARTMENT_ADMIN)) return true;
  return false;
}
